// The microphone: getUserMedia -> AudioWorklet (ScriptProcessor only where there is no
// worklet) -> 16 kHz mono PCM16 little-endian, in frames of about 100 ms. Each frame also
// reports an RMS level for the meter and for barge-in. Nothing here knows about vendors.

const TARGET_RATE = 16000;
const FRAME_SAMPLES = TARGET_RATE / 10;

// Held as a string and loaded from a Blob URL, so the worklet needs no separate file.
const WORKLET_SOURCE = `
class PcmTap extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) { this.port.postMessage(channel.slice(0)); }
    return true;
  }
}
registerProcessor('pcm-tap', PcmTap);`;

/** Averages input samples into 16 kHz PCM16 and cuts the result into ~100 ms frames. */
function makeDownsampler(
  inRate: number,
  outRate: number,
): (input: Float32Array) => Int16Array[] {
  let carry = new Float32Array(0);
  const pending: Int16Array[] = [];
  let pendingLength = 0;
  const ratio = inRate / outRate;
  return (input) => {
    const buffer = new Float32Array(carry.length + input.length);
    buffer.set(carry);
    buffer.set(input, carry.length);
    const outLength = Math.floor(buffer.length / ratio);
    const out = new Int16Array(outLength);
    for (let i = 0; i < outLength; i += 1) {
      const from = Math.floor(i * ratio);
      const to = Math.min(buffer.length, Math.floor((i + 1) * ratio));
      let sum = 0;
      for (let j = from; j < to; j += 1) {
        sum += buffer[j] ?? 0;
      }
      const value = Math.max(-1, Math.min(1, sum / Math.max(1, to - from)));
      out[i] = value < 0 ? value * 32768 : value * 32767;
    }
    carry = buffer.slice(Math.floor(outLength * ratio));
    pending.push(out);
    pendingLength += out.length;
    const frames: Int16Array[] = [];
    while (pendingLength >= FRAME_SAMPLES) {
      const frame = new Int16Array(FRAME_SAMPLES);
      let filled = 0;
      while (filled < FRAME_SAMPLES) {
        const head = pending[0] as Int16Array;
        const take = Math.min(head.length, FRAME_SAMPLES - filled);
        frame.set(head.subarray(0, take), filled);
        filled += take;
        if (take === head.length) {
          pending.shift();
        } else {
          pending[0] = head.subarray(take);
        }
      }
      pendingLength -= FRAME_SAMPLES;
      frames.push(frame);
    }
    return frames;
  };
}

function rms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const v = samples[i] ?? 0;
    sum += v * v;
  }
  return Math.sqrt(sum / Math.max(1, samples.length));
}

// The worklet may be registered once per context; a second start reuses it.
const loaded = new WeakSet<AudioContext>();

export type Capture = { stop: () => void };

/** Thrown when the microphone cannot be opened (denied, missing, or the worklet fails). */
export class MicrophoneUnavailable extends Error {}

/**
 * Starts the microphone on `audioContext`. `onFrame` gets each ~100 ms PCM16 frame; `onLevel`
 * gets the RMS (0..1, unscaled) of every chunk the browser hands over.
 */
export async function startCapture(
  audioContext: AudioContext,
  onFrame: (frame: Int16Array) => void,
  onLevel: (rms: number) => void,
): Promise<Capture> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch {
    throw new MicrophoneUnavailable('microphone not available');
  }
  let source: MediaStreamAudioSourceNode | null = null;
  let node: AudioNode | null = null;
  let blobUrl: string | null = null;
  // Tracks first, and nothing here may throw: the microphone must always be released.
  const stop = (): void => {
    try {
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      // Nothing more to do.
    }
    try {
      if (node !== null && 'port' in node) {
        (node as AudioWorkletNode).port.onmessage = null;
      }
      if (node !== null && 'onaudioprocess' in node) {
        (node as ScriptProcessorNode).onaudioprocess = null;
      }
      node?.disconnect();
      source?.disconnect();
    } catch {
      // Already disconnected.
    }
    if (blobUrl !== null) {
      URL.revokeObjectURL(blobUrl);
    }
  };
  try {
    source = audioContext.createMediaStreamSource(stream);
    const downsample = makeDownsampler(audioContext.sampleRate, TARGET_RATE);
    const handle = (samples: Float32Array): void => {
      onLevel(rms(samples));
      for (const frame of downsample(samples)) {
        onFrame(frame);
      }
    };
    if (audioContext.audioWorklet !== undefined) {
      if (!loaded.has(audioContext)) {
        blobUrl = URL.createObjectURL(
          new Blob([WORKLET_SOURCE], { type: 'application/javascript' }),
        );
        await audioContext.audioWorklet.addModule(blobUrl);
        loaded.add(audioContext);
      }
      const worklet = new AudioWorkletNode(audioContext, 'pcm-tap', {
        numberOfOutputs: 0,
      });
      worklet.port.onmessage = (event: MessageEvent<Float32Array>) => handle(event.data);
      source.connect(worklet);
      node = worklet;
    } else {
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (event) => handle(event.inputBuffer.getChannelData(0));
      source.connect(processor);
      processor.connect(audioContext.destination);
      node = processor;
    }
  } catch {
    stop();
    throw new MicrophoneUnavailable('microphone not available');
  }
  return { stop };
}
